import { useMemo, useState } from "react";

import ace from "brace";
import "brace/mode/json";
import "brace/theme/github";
import { JsonEditor } from "jsoneditor-react";
import { debounce } from "lodash";
import {
  Builder,
  Query,
  type ImmutableTree,
} from "react-awesome-query-builder";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  MATCHER_CONFIG,
  jsonLogicToTree,
  treeToJsonLogic,
} from "@pages/rules/matcher-config";
import { SecondaryButton } from "@shared/buttons";

import { createEmptyMatcher } from "../utils";

import { RegexTips } from "./regex-tips";

interface Props {
  matcher: any;
  error: string | null;
  onChange: (value: any) => void;
}

export const MatcherEditor = ({ matcher, error, onChange }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();

  const [tree, setTree] = useState(() => jsonLogicToTree(matcher));
  const [isRaw, setIsRaw] = useState(false);

  const debouncedOnChangeTree = useMemo(
    () =>
      debounce((value: ImmutableTree) => {
        onChange(treeToJsonLogic(value));
      }, 500),
    [onChange],
  );

  const debouncedOnChangeRaw = useMemo(
    () =>
      debounce((value: any) => {
        const toSet = value || createEmptyMatcher();
        try {
          setTree(jsonLogicToTree(toSet));
        } catch (e) {
          return;
        }

        onChange(toSet);
      }, 500),
    [onChange],
  );

  const renderBuilder = useMemo(
    () => (props: any) =>
      (
        <div>
          <div className="query-builder-container">
            <div className="query-builder qb-lite ml-0 mr-0">
              <Builder {...props} />
            </div>
          </div>
        </div>
      ),
    [],
  );

  return (
    <>
      <h4 className="m-0 text-xl font-bold mb-2">Rule</h4>
      {user.isAdmin && (
        <>
          {isRaw ? (
            <SecondaryButton
              className="font-bold flex items-center gap-2"
              onClick={() => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo:
                    "Change segment editor to structured moda",
                });

                setIsRaw(false);
              }}
            >
              <i className="fa-regular fa-block-quote text-cosmic" />
              VIEW STRUCTURED
            </SecondaryButton>
          ) : (
            <SecondaryButton
              className="font-bold flex items-center gap-2"
              onClick={() => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo: "Change segment editor to raw moda",
                });

                setIsRaw(true);
              }}
            >
              <i className="fa-regular fa-code text-cosmic" />
              VIEW RAW
            </SecondaryButton>
          )}
        </>
      )}
      <div className="mt-4">
        {isRaw ? (
          <JsonEditor
            value={matcher}
            onChange={debouncedOnChangeRaw}
            mode="code"
            ace={ace}
            htmlElementProps={{ style: { height: 600 } }}
          />
        ) : (
          <div className="react-awesome-query-builder">
            <Query
              {...MATCHER_CONFIG}
              value={tree}
              onChange={(value) => {
                setTree(value);
                debouncedOnChangeTree(value);
              }}
              renderBuilder={renderBuilder}
            />
          </div>
        )}
        {error && <div className="text-xxs text-mars mt-1">*{error}</div>}
      </div>
      <div className="mt-4">
        <RegexTips />
      </div>
    </>
  );
};
